import React, { useContext, useState } from 'react';
import { postcodeValidator } from 'postcode-validator';
import debounce from 'debounce';
import { BsTelephone } from 'react-icons/bs';
import { FaLink } from 'react-icons/fa6';
import { Link } from 'gatsby';

import { LocaleContext } from '@repo/contexts/LocaleContext';
import createOpenGraphTags from '@repo/utils/createOpenGraphTags';

import PageWrapper from '@repo/ui-components/page-wrapper/PageWrapper';
import IconSearch from '@repo/ui-components/svgs/search-icon.svg';

import Layout from '@components/layout';
import StyledSpinner from '@components/StyledSpinner';

// anticipate bad country data from blue/sanity
const countryMapping = {
  BHS: 'Bahamas',
  BMU: 'Bermuda',
  Canada: 'Canada',
  CANADA: 'Canada',
  CA: 'Canada',
  CAN: 'Canada',
  'GREAT BRITAIN': 'Great Britain',
  TTO: 'Trinidad and Tobago',
  USA: 'United States',
};

export interface DealerProps {
  hit: DealerData | DealerSearchResult;
  countryProp?: string;
}

function Dealer({ hit, countryProp = '' }: Readonly<DealerProps>) {
  return (
    <div className="relative flex flex-col h-full rounded-md p-4 bg-muted flex-1">
      <Link
        to={
          countryProp === 'Canada'
            ? `/en-ca/dealer/${hit.slug}`
            : `/dealer/${hit.slug}`
        }
        className="absolute top-4 right-4 text-card-foreground opacity-65 hover:opacity-100"
      >
        <FaLink size={20} />
      </Link>

      <h2 className="font-bold text-xl pr-6">
        <Link
          to={
            countryProp === 'Canada'
              ? `/en-ca/dealer/${hit.slug}`
              : `/dealer/${hit.slug}`
          }
        >
          {hit.name}
        </Link>
      </h2>

      {hit.phone && (
        <p className="p-0 my-1">
          <BsTelephone
            size={24}
            className="text-primary mr-2"
            aria-hidden="true"
          />
          <a href={`tel:${hit.phone}`}>{hit.phone}</a>
        </p>
      )}
      {hit.mainServiceArea && (
        <p className="p-0 my-1 line-clamp-2">
          <strong>Main Service Areas: </strong> {hit.mainServiceArea}
        </p>
      )}
      {hit.address?.state && (
        <p className="p-0 my-1">
          <strong>
            {countryMapping[hit.address.country] === 'Canada'
              ? 'State/Province: '
              : 'State: '}
          </strong>{' '}
          {hit.address.state}
        </p>
      )}
      {hit.address?.country && countryMapping[hit.address.country] && (
        <p className="p-0 my-1">
          <strong>Country: </strong> {countryMapping[hit.address.country]}
        </p>
      )}
    </div>
  );
}

async function fetchDealerData(zipcode) {
  try {
    const response = await fetch(
      `${process.env.GATSBY_POSTCODE_SEARCH_API}?postalCode=${zipcode}`,
      {
        method: 'GET',
      }
    );

    const data = await response.json();

    return data;
  } catch (error) {
    //  return error;
    throw new Error(
      'Error communicating with dealer search service, please try again later.'
    );
  }
}

function isValidUSOrCAPostalCode(searchTerm) {
  return (
    postcodeValidator(searchTerm, 'CA') || postcodeValidator(searchTerm, 'US')
  );
}

function validateSearchTerm(searchTerm) {
  return searchTerm.length > 0 && isValidUSOrCAPostalCode(searchTerm);
}

function formatSearchTermForPostalCode(searchTerm) {
  if (postcodeValidator(searchTerm, 'CA')) {
    // Format Canadian postal code with space in the middle
    return searchTerm.replace(/ /g, '').replace(/(.{3})/, '$1 ');
  }
  // For US or unformatted CA postal code, return as is
  return searchTerm;
}

type DealerData = {
  objectID: string;
  name: string;
  slug: string;
  phone: string;
  email: string;
  mainServiceArea: string;
  address: {
    state: string;
    country: string;
  };
};

type DealerSearchResult = {
  address: {
    city: string;
    state: string;
    zip: string;
    country: string;
    line1: string;
    line2?: string;
    line3?: string;
  };
  dealerID: string;
  email: string;
  id: string;
  isCompanyOwned: boolean;
  mainServiceArea: string;
  name: string;
  phone: string;
  slug: string;
};
export interface SearchPageProps {
  countryProp?: string;
  initialDealerData: DealerData[];
}

export default function SearchPage({
  countryProp = '',
  initialDealerData,
}: Readonly<SearchPageProps>) {
  const localeContext = useContext(LocaleContext);

  const [formHasValidationError, setFormHasValidationError] = useState(false);
  const [commsError, setCommsError] = useState('');
  const [searchResults, setSearchResults] = useState<DealerSearchResult[]>([]);
  const [showSpinner, setShowSpinner] = useState(false);
  const [searchTriggered, setSearchTriggered] = useState(false);

  const countryMappings = {
    Canada: ['ca', 'canada', 'can'],
    Other: [
      'us',
      'usa',
      'united states',
      'great britain',
      'bermuda',
      'trinidad and tobago',
    ],
  };

  const isCountryMatch = (dealerCountry, allowedCountries) => {
    const normalizedCountry = dealerCountry?.toLowerCase();
    return allowedCountries.some(country => country === normalizedCountry);
  };

  const filteredDealerData = initialDealerData.filter(dealer => {
    const dealerCountry = dealer.address?.country;
    if (countryProp === 'Canada') {
      return isCountryMatch(dealerCountry, countryMappings.Canada);
    }
    return isCountryMatch(dealerCountry, countryMappings.Other);
  });

  async function searchDealersByPostalCode(postalCode) {
    const formattedPostalCode = formatSearchTermForPostalCode(postalCode);

    try {
      const results = await fetchDealerData(formattedPostalCode);
      setSearchResults(results);
    } catch (error) {
      setCommsError(error.message);
    } finally {
      setShowSpinner(false);
    }
  }

  function initializeSearchUI() {
    setShowSpinner(true);
    setSearchTriggered(true);
    setCommsError('');
    setSearchResults([]);
    setFormHasValidationError(false);
  }

  function handleInvalidSearchTerm() {
    setFormHasValidationError(true);
    setShowSpinner(false);
  }

  async function handleFormSubmit(event) {
    event.preventDefault();

    const postalCode = event.target.value.toUpperCase().trim();

    // Initialize UI for search
    initializeSearchUI();

    if (!validateSearchTerm(postalCode)) {
      handleInvalidSearchTerm();
      return;
    }

    await searchDealersByPostalCode(postalCode);
  }

  return (
    <Layout
      headTagData={{
        title: 'Our Network of Local Dealers | Invisible Fence',
        description:
          'Invisible Fence Dealers: Local Experts in Pet Safety Solutions & Support.',
        meta: createOpenGraphTags(
          {
            title: 'Our Network of Local Dealers | InvisibleFence',
            description:
              'Invisible Fence Dealers: Local Experts in Pet Safety Solutions & Support.',
            url: '/dealers',
          },
          'invisiblefence'
        ),
      }}
    >
      <PageWrapper>
        <h1>Our Network of Local Dealers</h1>
        <p>
          One big reason Invisible Fence® Brand has been the industry leader for
          over 50 years lies with our network of trained professionals who
          install the system, train your pets to their boundaries, and provide
          full support and service when you need it. They’re ready to help you
          find the right solutions to keep your pets happy and safe, so find
          your local dealer today and take the next steps towards pet safety and
          freedom.
        </p>

        <div className="flex mb-2 flex-col" id="search-form-container">
          <label htmlFor="search-box" className="mb-1 font-bold text-lg">
            {localeContext.getCountryCode() === 'CA'
              ? 'Search by postal code'
              : 'Search by ZIP/postal code'}
          </label>
          <form
            id="search-box"
            className="flex flex-1 h-12 relative items-center"
            aria-label="Search by ZIP/postal code"
            onChange={debounce(e => handleFormSubmit(e), 200)}
            onSubmit={e => e.preventDefault()}
          >
            <IconSearch
              width={16}
              height={16}
              className="absolute fill-card-foreground left-4 top-1/2 transform -translate-y-1/2"
              aria-hidden="true"
            />
            <input
              type="text"
              name="searchTerm"
              className="flex-1 h-12 text-base rounded-none shadow-none pl-10 text-input border-input"
            />
          </form>
          {formHasValidationError && (
            <div className="h-16 pt-4 flex items-center">
              Invalid postal code
            </div>
          )}
          {commsError && (
            <div className="h-16 pt-4 flex items-center">{commsError}</div>
          )}
          {showSpinner && (
            <div className="h-16 pt-4 flex items-center">
              <StyledSpinner />
            </div>
          )}

          {
            // if no results, show message
            searchResults.length === 0 &&
              !showSpinner &&
              !formHasValidationError &&
              !commsError &&
              searchTriggered && (
                <div className="h-16 pt-4 flex items-center">
                  No results found
                </div>
              )
          }
        </div>

        {searchResults.length > 0 && (
          <div className="my-0 py-4">
            <ul className="grid grid-cols-1 gap-8 auto-rows-[1fr] sm:grid-cols-2 lg:grid-cols-3 m-0 p-0">
              {searchResults.map((hit: DealerSearchResult) => (
                <li
                  key={hit.dealerID}
                  className="flex items-start m-0 p-0 shadow-none"
                >
                  <Dealer hit={hit} countryProp={countryProp} />
                </li>
              ))}
            </ul>
          </div>
        )}

        {!searchTriggered && (
          <div className="my-0 py-4">
            <ul className="grid grid-cols-1 gap-8 auto-rows-[1fr] sm:grid-cols-2 lg:grid-cols-3 m-0 p-0">
              {filteredDealerData.map((hit: DealerData) => (
                <li
                  key={hit.objectID}
                  className="flex items-start m-0 p-0 shadow-none"
                >
                  <Dealer hit={hit} />
                </li>
              ))}
            </ul>
          </div>
        )}
      </PageWrapper>
    </Layout>
  );
}
